/**
 * Segment writes that CARRY A RULE.
 *
 * LAYER 1, on top of `./store.ts`. Everything here does something a plain
 * `put` would get wrong: a task-alive guard, a renumber, an atomic swap, a
 * "blank is not an answer" check, or a field DELETION (`updateSegment` merges
 * and can never remove a key).
 *
 * The split from `./store.ts` is the point: storage does not know what a
 * segment means, and these functions do. They stay inside `src/db/` rather
 * than moving to `services/` because the db barrel must never import upward
 * from services — see the layer rules in ARCHITECTURE.md.
 */

import type { Segment, TaskStatus, VerifyState } from '../types';
import { emitDbChange, getDB, getTask, listTasks, putTask, updateSegment } from './store';
import { reconcileTask } from './maintenance';

/* ------------------------------------------------------------------ */
/* Field deletion — the primitive the three "undo" writers share       */
/* ------------------------------------------------------------------ */

/**
 * Remove keys from a segment (optionally alongside a merge patch), in ONE
 * transaction.
 *
 * `updateSegment` shallow-merges and therefore can never remove a field, so
 * every "undo" in the app needs read-modify-write with a `delete`. Three
 * writers did that by hand; this is the one implementation.
 *
 * When `keys` are all already absent AND there is no `patch`, this writes
 * nothing and fires no change event — the row is returned unchanged. That
 * no-op path is load-bearing for {@link clearRemovalSuggestion}.
 */
export async function clearSegmentFields(
  id: string,
  keys: Array<keyof Segment>,
  patch?: Partial<Segment>,
): Promise<Segment | undefined> {
  const db = await getDB();
  const tx = db.transaction('segments', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    return undefined;
  }
  const present = keys.filter((k) => existing[k] !== undefined);
  if (present.length === 0 && !patch) {
    await tx.done;
    return existing;
  }
  const next: Segment = { ...existing, ...patch, id, taskId: existing.taskId };
  for (const k of keys) delete next[k];
  await tx.store.put(next);
  await tx.done;
  emitDbChange();
  return next;
}

/**
 * The task's short display code ("A1"), minting it if the task predates codes.
 *
 * Clip names derive from it (A1.1, A1.2, …), so a recut on a legacy task needs
 * one before it can name anything. The code is derived from creation order and
 * persisted on first use, so it is stable from then on.
 *
 * Was duplicated in `recut.nextNames` and the old `firstPass`; both now call
 * this. It lives in the db layer because it WRITES the task row.
 */
export async function ensureTaskCode(taskId: string): Promise<string | undefined> {
  const task = await getTask(taskId);
  let code = task?.code;
  if (!code && task) {
    // First recut of a legacy task: mint its code from its creation order.
    const all = await listTasks();
    code = `A${all.length - all.findIndex((t) => t.id === taskId)}`;
    await putTask({ ...task, code });
  }
  return code;
}

/**
 * Swap a task's whole segment set — old segments and clips out, new segments
 * and clips in — in ONE transaction.
 *
 * `deleteSegmentsForTask` + `putSegments` + `putClips` is three transactions,
 * and a failure in the middle one (a quota error is the realistic case, clips
 * are the bulky part) leaves the task with segments that have no playable clip,
 * or with no segments at all while the task still claims to be in `review`.
 * Atomic here means the first pass either lands completely or not at all.
 */
export async function replaceSegmentsForTask(
  taskId: string,
  entries: Array<{ segment: Segment; clip: Blob }>,
): Promise<void> {
  const db = await getDB();
  const staleIds = await db.getAllKeysFromIndex('segments', 'by-task', taskId);
  const tx = db.transaction(['segments', 'clips'], 'readwrite');
  const segments = tx.objectStore('segments');
  const clips = tx.objectStore('clips');
  await Promise.all([
    ...staleIds.map((sid) => segments.delete(sid)),
    ...staleIds.map((sid) => clips.delete(sid)),
    ...entries.map((e) => segments.put(e.segment)),
    ...entries.map((e) => clips.put(e.clip, e.segment.id)),
  ]);
  await tx.done;
  emitDbChange();
}

/** One segment to rewrite, optionally with a freshly cut clip to go with it. */
export interface SegmentRevision {
  segment: Segment;
  /** Replacement clip WAV. Omit to leave the stored clip alone. */
  clip?: Blob;
}

/**
 * Apply a partial rewrite of a task's segment set in ONE transaction:
 * `revisions` are written (segment row, plus its clip when the boundaries
 * moved), `deleteIds` are removed along with their clips.
 *
 * This is what the clip-QA loop uses to land its repairs. Unlike
 * {@link replaceSegmentsForTask} it does not touch segments it was not told
 * about, so a QA pass that only changed three clips rewrites three rows — and
 * a merge (rewrite the survivor, delete the absorbed neighbour, renumber the
 * tail) still lands atomically, never leaving two segments claiming the same
 * `index` or a clip orphaned from its segment.
 */
export async function reviseSegments(
  revisions: SegmentRevision[],
  deleteIds: string[] = [],
  dropClipIds: string[] = [],
): Promise<void> {
  if (revisions.length === 0 && deleteIds.length === 0 && dropClipIds.length === 0) return;
  const db = await getDB();
  const tx = db.transaction(['tasks', 'segments', 'clips'], 'readwrite');
  const tasks = tx.objectStore('tasks');
  const segments = tx.objectStore('segments');
  const clips = tx.objectStore('clips');
  // A long-running QA/re-segmentation pass may land after its task was deleted
  // mid-flight; writing then would recreate the rows as orphans that no
  // reconcile can ever clear. Deletes are always safe — only writes are gated.
  const taskIds = [...new Set(revisions.map((r) => r.segment.taskId))];
  const alive = new Set(
    (await Promise.all(taskIds.map(async (id) => ((await tasks.get(id)) ? id : null)))).filter(
      (id): id is string => id !== null,
    ),
  );
  const liveRevisions = revisions.filter((r) => alive.has(r.segment.taskId));
  if (liveRevisions.length < revisions.length) {
    console.warn(
      `reviseSegments: dropped ${revisions.length - liveRevisions.length} revision(s) for deleted task(s)`,
    );
  }
  await Promise.all([
    ...deleteIds.map((sid) => segments.delete(sid)),
    ...deleteIds.map((sid) => clips.delete(sid)),
    // Obsolete records keep their row (stamps + words) but lose their cached
    // audio — clips are views into the one original file, not files of record.
    ...dropClipIds.map((sid) => clips.delete(sid)),
    ...liveRevisions.map((r) => segments.put(r.segment)),
    ...liveRevisions.filter((r) => r.clip).map((r) => clips.put(r.clip as Blob, r.segment.id)),
  ]);
  await tx.done;
  emitDbChange();
}

/* ------------------------------------------------------------------ */
/* Verify / Correct verdicts                                           */
/* ------------------------------------------------------------------ */

/** Verify tab: record the Correct / Incorrect verdict. */
export function setSegmentVerify(id: string, verify: VerifyState): Promise<Segment | undefined> {
  return updateSegment(id, { verify });
}

/**
 * Correct tab: save a human correction. Passing text and/or kind is enough to
 * make the segment count as resolved for reconciliation.
 */
export function correctSegment(
  id: string,
  correction: { text?: string; kind?: Segment['kind'] },
): Promise<Segment | undefined> {
  const patch: Partial<Segment> = {};
  // A blank correction is not a correction: `isSegmentResolved` treats an empty
  // string as "no correction", so storing one would leave the segment stuck in
  // the Correct queue forever while looking answered.
  const text = correction.text?.trim();
  if (text) patch.correctedText = text;
  if (correction.kind !== undefined) patch.correctedKind = correction.kind;
  if (patch.correctedText === undefined && patch.correctedKind === undefined) {
    return Promise.resolve(undefined);
  }
  return updateSegment(id, patch);
}

/**
 * Put a segment back in the verify queue and throw away any human correction.
 *
 * `updateSegment` can only merge fields, never remove them, so an "undo" done
 * through `setSegmentVerify(id, 'pending')` would leave a stale `correctedText`
 * on a pending segment — and `isSegmentResolved` would then count it as done
 * the moment it was marked incorrect again. This is the only correct way to
 * re-open a segment.
 */
export function reopenSegment(id: string): Promise<Segment | undefined> {
  return clearSegmentFields(id, ['correctedText', 'correctedKind'], { verify: 'pending' });
}

/**
 * Throw away the pipeline's "this row should not exist" suggestion.
 *
 * Same shape of problem as {@link reopenSegment}: `updateSegment` merges and can
 * never remove a field, so a human who says "no, that word is real" would keep
 * seeing the duplicate hint on the card forever.
 */
export function clearRemovalSuggestion(id: string): Promise<Segment | undefined> {
  return clearSegmentFields(id, ['suggestedRemoval']);
}

/* ------------------------------------------------------------------ */
/* Removing a segment a human says should not exist                    */
/* ------------------------------------------------------------------ */

/** What {@link removeSegment} did, for the page that asked for it. */
export interface SegmentRemoval {
  taskId: string;
  /** The `index` the removed segment held. */
  removedIndex: number;
  /** How many segments the task has left. */
  remaining: number;
  /** How many of those had to be renumbered. */
  renumbered: number;
  /** The task's status after reconciliation. */
  status?: TaskStatus;
}

/**
 * Delete ONE segment and its clip, then close the hole in `index`.
 *
 * The gap in the numbering is not cosmetic: `index` is the app's only notion of
 * time order for a task (every queue sorts by it, the Verify card shows it as
 * "#n" and the transcript walks it), and a first pass guarantees it is
 * contiguous from 0. A plain delete would leave 0,1,3,4 — which nothing crashes
 * on, and which quietly makes "#4 of 72" a lie and every later renumbering
 * (a merge, a re-run) start from a broken premise.
 *
 * Delete + renumber go through {@link reviseSegments} as ONE transaction, so a
 * failure can never leave the task with a clip whose segment is gone or two
 * segments claiming the same index. Reconciliation runs after, because removing
 * the last unresolved row of a task legitimately completes it.
 */
export async function removeSegment(segmentId: string): Promise<SegmentRemoval | undefined> {
  const db = await getDB();
  const seg = await db.get('segments', segmentId);
  if (!seg) return undefined;

  // TODO(partition): this renumbers the UNFILTERED sibling set — unlike every
  // other sibling operation in the app, it does NOT restrict to active leaves
  // (`types.isActiveLeaf`). On a task that has been recut, superseded rows are
  // therefore counted and renumbered alongside live ones, which is how `index`
  // and `startSec` drift apart (see Segment.index). Left EXACTLY as it was:
  // adding the filter changes which rows get written, i.e. behavior. See F3.
  const siblings = (await db.getAllFromIndex('segments', 'by-task', seg.taskId)).sort(
    (a, b) => a.index - b.index,
  );
  const survivors = siblings.filter((s) => s.id !== segmentId);
  const revisions: SegmentRevision[] = [];
  survivors.forEach((s, index) => {
    if (s.index !== index) revisions.push({ segment: { ...s, index } });
  });

  await reviseSegments(revisions, [segmentId]);
  const status = await reconcileTask(seg.taskId);
  return {
    taskId: seg.taskId,
    removedIndex: seg.index,
    remaining: survivors.length,
    renumbered: revisions.length,
    status,
  };
}

/* ------------------------------------------------------------------ */
/* The Cuts station (stage 1)                                          */
/* ------------------------------------------------------------------ */
/* The queue these two feed is `getPendingCutSegments` in ./queues.ts.  */

/** Stage-1 sign-off: this clip's boundaries are right; let it flow onward. */
export async function approveCut(segmentId: string): Promise<Segment | undefined> {
  const db = await getDB();
  const seg = await db.get('segments', segmentId);
  if (!seg) return undefined;
  const updated: Segment = { ...seg, cutApprovedAt: Date.now() };
  await db.put('segments', updated);
  emitDbChange();
  return updated;
}

/**
 * Send a clip back to the Cuts station (its boundaries are in doubt).
 *
 * NOT WIRED: nothing calls this today. The rule `Segment.cutApprovedAt`
 * documents — "any boundary change clears it" — is therefore UNENFORCED;
 * `moveBoundary`, `setBoundaries` and `mergeAdjacentSegments` all carry the
 * stamp through a span change. See ARCHITECTURE.md.
 */
export function revokeCutApproval(segmentId: string): Promise<Segment | undefined> {
  return clearSegmentFields(segmentId, ['cutApprovedAt', 'correctedText', 'correctedKind'], {
    verify: 'pending',
  });
}
